import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import {
  ArrowLeft, MessageCircle, Brain, Target, Sparkles, ShieldCheck, Zap,
  Smartphone, Apple, BarChart3, Crosshair, Download, ArrowRight, Send, Loader2, Lock, Cpu,
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import NeuralBackground from '@/components/NeuralBackground';
import { APP_STORE_URL } from '@/lib/appStoreUrl';
import './Clayo.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const DEMO_USED_KEY = 'cp_clayo_demo_used';

const DEMO_SUGGESTIONS = [
  "Comment progresser sur ma série 1 au Skeet ?",
  "Quel choke choisir pour le Trap ACO ?",
  "Comment gérer le stress en compétition ?",
];

const CLAYO_IMG = '/assets/img/clayo-mascot.jpg';
const SCREEN_1 = '/assets/img/clayo-chat-1.jpg';
const SCREEN_2 = '/assets/img/clayo-chat-2.jpg';

const features = [
  {
    icon: Brain,
    title: 'Analyse tes performances',
    desc: "Clayo lit tes sessions, repère tes séries faibles, tes plateaux problématiques et te dit exactement où tu perds des points.",
  },
  {
    icon: Crosshair,
    title: 'Conseils matériel personnalisés',
    desc: "Demande-lui si ton fusil, tes chokes ou tes cartouches sont adaptés à une discipline. Il croise marque, modèle et profil de tir.",
  },
  {
    icon: Target,
    title: 'Plan de progression concret',
    desc: "Pas de blabla : 3 conseils actionnables, classés par impact. Échauffement, position, mental — adaptés à tes données réelles.",
  },
  {
    icon: BarChart3,
    title: 'Mémoire de tes sessions',
    desc: "Clayo se souvient de ton historique. Plus tu tires, plus ses conseils deviennent précis et personnalisés.",
  },
  {
    icon: ShieldCheck,
    title: '100% confidentiel',
    desc: "Tes données restent privées. Aucune info partagée, aucun profilage à des fins publicitaires.",
  },
  {
    icon: Zap,
    title: 'Disponible 24/7',
    desc: "Sur le pas de tir ou chez toi le soir : pose ta question, reçois une réponse experte en quelques secondes.",
  },
];

const examples = [
  {
    img: SCREEN_1,
    caption: "« Comment progresser sur la série 1 ? »",
    desc: "Clayo détecte ta moyenne sur chaque série, identifie ton point faible et te donne 3 conseils concrets — échauffement, routine mentale, visualisation.",
  },
  {
    img: SCREEN_2,
    caption: "« Mon fusil est-il adapté au Skeet ? »",
    desc: "Clayo connaît ton Arsenal. Il analyse ton Benelli 828U, valide ses points forts pour le Skeet et te recommande la bonne configuration de chokes.",
  },
];

export default function Clayo() {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  // Streamed/typewriter display of the answer for a futuristic feel.
  const [displayedAnswer, setDisplayedAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [used, setUsed] = useState(() => {
    try { return localStorage.getItem(DEMO_USED_KEY) === '1'; } catch { return false; }
  });

  // Type the answer character by character (~ 18ms per char) once it arrives.
  useEffect(() => {
    if (!answer) { setDisplayedAnswer(''); return; }
    setDisplayedAnswer('');
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setDisplayedAnswer(answer.slice(0, i));
      if (i >= answer.length) clearInterval(id);
    }, 18);
    return () => clearInterval(id);
  }, [answer]);

  const askClayo = async () => {
    const q = question.trim();
    if (!q || used || loading) return;
    setLoading(true);
    setError('');
    setAnswer('');
    try {
      const res = await fetch(`${API_BASE}/api/clayo/demo-ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q }),
      });
      let data = null;
      try { data = await res.json(); } catch (_) { /* non-JSON response */ }
      if (!res.ok) {
        if (res.status === 429) {
          setUsed(true);
          try { localStorage.setItem(DEMO_USED_KEY, '1'); } catch (e) {/* noop */}
          setError(data?.message || 'Tu as déjà posé ta question du jour.');
        } else if (res.status === 502 || res.status === 503 || res.status === 504) {
          setError(data?.error || "Clayo est momentanément indisponible. Réessaie dans quelques secondes.");
        } else if (res.status === 400) {
          setError(data?.error || "Question invalide.");
        } else {
          setError(data?.error || `Erreur réseau (${res.status}). Réessaie dans un instant.`);
        }
        return;
      }
      setAnswer(data?.answer || '');
      setUsed(true);
      try { localStorage.setItem(DEMO_USED_KEY, '1'); } catch (e) {/* noop */}
    } catch {
      setError('Connexion impossible. Vérifie ton réseau puis réessaie.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <Navbar />

      {/* === Hero — futuristic AI === */}
      <section className="relative pt-32 sm:pt-40 pb-20 overflow-hidden">
        {/* Animated neural network canvas */}
        <div className="absolute inset-0 pointer-events-none opacity-70">
          <NeuralBackground />
        </div>
        {/* Animated grid overlay */}
        <div className="absolute inset-0 pointer-events-none cp-grid-bg" />
        {/* Subtle vignette so content stays readable */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#0D0D0D] via-transparent to-[#0D0D0D]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Link
            to="/"
            data-testid="clayo-back-home"
            className="inline-flex items-center gap-2 text-[#A0A0A0] hover:text-white text-sm mb-12 transition-colors"
          >
            <ArrowLeft size={16} /> Retour à l&apos;accueil
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              {/* AI status badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 mb-6">
                <Cpu size={13} className="text-[#FF6B35]" />
                <span className="text-[#FF6B35] text-[11px] font-semibold uppercase tracking-[0.2em]">
                  Coach IA ball-trap
                </span>
                <span className="cp-ai-dot ml-1" aria-hidden="true" />
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold font-['Poppins'] leading-[1.05] mb-6">
                Voici{' '}
                <span className="cp-title-glow font-black">Clayo</span>
              </h1>

              <p className="text-[#A0A0A0] text-lg sm:text-xl leading-relaxed mb-8 max-w-xl">
                Le premier coach IA dédié au ball-trap. Il analyse tes sessions, comprend ton matériel
                et te donne des conseils précis pour progresser — directement intégré à l&apos;application ClayPulse.
              </p>

              {/* Tech specs strip (purely visual) */}
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  { label: 'Réponse < 5s' },
                  { label: 'Modèle Gemini 3' },
                  { label: 'Mémoire contextuelle' },
                ].map((spec, i) => (
                  <motion.span
                    key={spec.label}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                    className="text-[11px] uppercase tracking-wider text-white/50 px-2.5 py-1 rounded-md border border-white/10 bg-white/[0.02]"
                  >
                    {spec.label}
                  </motion.span>
                ))}
              </div>

              {/* Availability */}
              <div className="flex flex-wrap gap-3 mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30">
                  <Smartphone size={14} className="text-green-400" />
                  <span className="text-green-400 text-sm font-medium">Disponible sur Android</span>
                </div>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 hover:bg-green-500/20 transition"
                >
                  <Apple size={14} className="text-green-400" />
                  <span className="text-green-400 text-sm font-medium">Disponible sur l&apos;App Store</span>
                </a>
              </div>

              <div className="flex flex-wrap gap-3">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="clayo-download-ios"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white font-semibold hover:shadow-[0_0_30px_rgba(255,107,53,0.4)] transition-all duration-300 hover:scale-105"
                >
                  <Apple size={18} />
                  Télécharger sur l&apos;App Store
                </a>
                <Link
                  to="/android"
                  data-testid="clayo-download-android"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 transition-all"
                >
                  <Download size={18} />
                  Version Android
                </Link>
              </div>
            </motion.div>

            {/* Mascot with sci-fi orbits + scanline */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="relative flex justify-center"
            >
              <div className="relative w-[320px] sm:w-[420px] lg:w-[480px] aspect-square">
                {/* Orbiting rings */}
                <div className="cp-orbit" />
                <div className="cp-orbit-2" />
                {/* Image with scanline overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <img
                      src={CLAYO_IMG}
                      alt="Clayo, mascotte coach IA ClayPulse"
                      data-testid="clayo-hero-img"
                      className="relative w-[280px] sm:w-[360px] lg:w-[420px] h-auto object-contain mix-blend-lighten"
                    />
                    <span className="cp-scanline" aria-hidden="true" />
                  </div>
                </div>
                {/* HUD corner brackets */}
                {[
                  { pos: 'top-0 left-0',     b: 'border-t-2 border-l-2' },
                  { pos: 'top-0 right-0',    b: 'border-t-2 border-r-2' },
                  { pos: 'bottom-0 left-0',  b: 'border-b-2 border-l-2' },
                  { pos: 'bottom-0 right-0', b: 'border-b-2 border-r-2' },
                ].map((c, i) => (
                  <span
                    key={i}
                    className={`absolute ${c.pos} w-6 h-6 ${c.b} border-[#FF6B35]/40`}
                    aria-hidden="true"
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Clayo does */}
      <section className="relative py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Poppins'] mb-4">
              Un vrai coach, <span className="text-[#FF6B35]">pas un simple chatbot</span>
            </h2>
            <p className="text-[#A0A0A0] text-base sm:text-lg">
              Clayo lit tes données ClayPulse en temps réel. Ses réponses sont basées sur tes scores,
              ton matériel et ton historique — pas sur des généralités.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`clayo-feature-${i}`}
                className="cp-feature-card group relative p-6 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-[#FF6B35]/30 hover:bg-white/[0.05] transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-[#FF6B35]/10 border border-[#FF6B35]/20 flex items-center justify-center mb-4 group-hover:bg-[#FF6B35]/20 transition-colors">
                  <f.icon size={20} className="text-[#FF6B35]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2 font-['Poppins']">{f.title}</h3>
                <p className="text-[#A0A0A0] text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Real examples */}
      <section className="relative py-20 sm:py-28 border-t border-white/5 bg-gradient-to-b from-transparent to-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 mb-4">
              <MessageCircle size={14} className="text-[#FF6B35]" />
              <span className="text-[#FF6B35] text-xs font-semibold uppercase tracking-wider">Exemples réels</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Poppins'] mb-4">
              Comment ça se passe <span className="text-[#FF6B35]">dans l'app</span>
            </h2>
            <p className="text-[#A0A0A0] text-base sm:text-lg">
              Tu poses ta question dans l'onglet Clayo, il te répond avec des données précises issues de ton historique.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
            {examples.map((ex, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                data-testid={`clayo-example-${i}`}
                className="flex flex-col items-center text-center"
              >
                <div className="relative mb-6 group">
                  <div className="absolute -inset-4 bg-gradient-to-br from-[#FF6B35]/20 to-transparent rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative rounded-[2.5rem] overflow-hidden border-[6px] border-[#1a1a1a] shadow-2xl bg-black max-w-[280px]">
                    <img
                      src={ex.img}
                      alt={ex.caption}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <p className="text-[#FF6B35] font-semibold text-base sm:text-lg mb-3 italic">
                  {ex.caption}
                </p>
                <p className="text-[#A0A0A0] text-sm sm:text-base leading-relaxed max-w-md">
                  {ex.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive demo — 1 free question */}
      <section className="relative py-20 sm:py-28 border-t border-white/5" data-testid="clayo-demo-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 mb-4">
              <Sparkles size={14} className="text-[#FF6B35]" />
              <span className="text-[#FF6B35] text-xs font-semibold uppercase tracking-wider">Essaie maintenant</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Poppins'] mb-4">
              Pose <span className="text-[#FF6B35]">une question</span> à Clayo
            </h2>
            <p className="text-[#A0A0A0] text-base sm:text-lg max-w-2xl mx-auto">
              Une seule question gratuite, sans inscription. Pour discuter sans limite avec Clayo, télécharge l'application ClayPulse.
            </p>
          </div>

          <div className="relative rounded-2xl bg-white/[0.03] border border-white/10 p-5 sm:p-6">
            {/* Suggestions */}
            {!used && !answer && (
              <div className="flex flex-wrap gap-2 mb-4">
                {DEMO_SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid="clayo-demo-suggestion"
                    onClick={() => setQuestion(s)}
                    className="text-xs sm:text-sm px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[#A0A0A0] hover:text-white hover:border-[#FF6B35]/40 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input row */}
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') askClayo(); }}
                placeholder={used ? 'Question déjà utilisée' : 'Pose ta question à Clayo…'}
                maxLength={500}
                disabled={used || loading}
                data-testid="clayo-demo-input"
                className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF6B35]/60 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                onClick={askClayo}
                disabled={used || loading || !question.trim()}
                data-testid="clayo-demo-submit"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_25px_rgba(255,107,53,0.4)] transition-all"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                {loading ? 'Analyse en cours…' : 'Envoyer'}
              </button>
            </div>

            <p className="text-white/40 text-xs mt-3 flex items-center gap-1.5">
              <Lock size={11} />
              1 question gratuite par jour. Ta question reste anonyme.
            </p>

            {/* Thinking loader — futuristic */}
            {loading && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                data-testid="clayo-demo-thinking"
                className="mt-6 rounded-xl bg-black/40 border border-[#FF6B35]/20 p-5 relative overflow-hidden"
              >
                <span className="cp-scanline" aria-hidden="true" />
                <div className="flex items-center gap-3 relative z-10">
                  <img src={CLAYO_IMG} alt="" className="w-9 h-9 object-contain mix-blend-lighten animate-pulse" />
                  <div className="flex-1">
                    <div className="text-white text-sm font-semibold mb-1">Clayo analyse ta question</div>
                    <div className="text-[#FF6B35] text-xs flex items-center gap-2">
                      <span className="cp-typing"><span /><span /><span /></span>
                      <span className="opacity-70 uppercase tracking-wider text-[10px]">Modèle neuronal actif</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Answer with streamed typewriter */}
            {answer && !loading && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                data-testid="clayo-demo-answer"
                className="mt-6 rounded-xl bg-black/40 border border-[#FF6B35]/20 p-5"
              >
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/5">
                  <img
                    src={CLAYO_IMG}
                    alt=""
                    className="w-9 h-9 object-contain mix-blend-lighten"
                  />
                  <div>
                    <div className="text-white text-sm font-semibold flex items-center gap-2">
                      Clayo
                      <span className="cp-ai-dot" aria-hidden="true" />
                    </div>
                    <div className="text-green-400 text-[10px] uppercase tracking-wider">Coach IA · Connecté</div>
                  </div>
                </div>
                <div className="text-white/90 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                  {displayedAnswer}
                  {displayedAnswer.length < answer.length && (
                    <span className="cp-cursor" aria-hidden="true" />
                  )}
                </div>
                <div className="mt-5 pt-4 border-t border-white/5 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                  <p className="text-[#A0A0A0] text-xs sm:text-sm">
                    Envie de continuer&nbsp;? Discute sans limite avec Clayo dans l&apos;app.
                  </p>
                  <Link
                    to="/android"
                    data-testid="clayo-demo-cta-download"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white text-sm font-semibold hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] transition shrink-0"
                  >
                    <Download size={14} /> Télécharger ClayPulse
                  </Link>
                </div>
              </motion.div>
            )}

            {/* Error */}
            {error && !answer && (
              <div
                data-testid="clayo-demo-error"
                className="mt-4 rounded-xl bg-red-500/10 border border-red-500/30 p-4 text-red-300 text-sm"
              >
                {error}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block mb-8">
              <img
                src={CLAYO_IMG}
                alt="Clayo"
                className="w-32 h-32 sm:w-40 sm:h-40 object-contain mx-auto mix-blend-lighten"
              />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Poppins'] mb-6">
              Prêt à tirer avec <span className="text-[#FF6B35]">un coach dans la poche ?</span>
            </h2>
            <p className="text-[#A0A0A0] text-base sm:text-lg mb-10 max-w-2xl mx-auto">
              Clayo est inclus dans ClayPulse Pro. Télécharge l'app et discute avec lui dès aujourd'hui.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/android"
                data-testid="clayo-final-cta-android"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white font-semibold hover:shadow-[0_0_30px_rgba(255,107,53,0.4)] transition-all duration-300 hover:scale-105"
              >
                <Download size={18} />
                Télécharger ClayPulse
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
